import type { FC, ReactNode } from 'react';
import { InspectIcon } from '@THC/components/Icons/Inspect';
import { MDText } from 'i18n-react';

import { Badge } from '@motorway/mw-highway-code';

import { formatShortDate } from 'Utilities/formatters';
import { numberWithCommas } from 'Utilities/helpers';

import LocalTexts from '../ServiceHistoryEnhancedSection.texts.json';

import styles from './ServiceHistoryItem.module.scss';

const LocalT = new MDText(LocalTexts);

interface ServiceHistoryItemProps {
	date: Nullable<string>;
	distanceOverdue: Nullable<number>;
	mileage: number;
	timeOverdue: Nullable<number>;
	type: string;
}

export const getServiceType = (type: string) =>
	({
		[type]: LocalT.translate('unknownCentre'),
		independent: LocalT.translate('serviceCentre.independent'), // eslint-disable-line
		main: LocalT.translate('serviceCentre.main'),
	})[type];

export const ServiceHistoryItem: FC<ServiceHistoryItemProps> = ({
	date,
	distanceOverdue,
	mileage,
	timeOverdue,
	type,
}) => {
	const isOverdueBadgeVisible = distanceOverdue || Number.isInteger(timeOverdue);

	let overdueTimeText: ReactNode | undefined;
	if (timeOverdue) {
		overdueTimeText = LocalT.translate('record.time', {
			pluralise: timeOverdue !== 1 ? 's' : '',
			time: timeOverdue,
		});
	} else if (timeOverdue === 0) {
		overdueTimeText = LocalT.translate('record.timeLessThanOne');
	}

	const overdue = [
		overdueTimeText,
		distanceOverdue
			? LocalT.translate('record.mileage', { mileage: numberWithCommas(distanceOverdue || 0) })
			: undefined,
	].filter(Boolean);
	return (
		<div className={styles.wrapper}>
			<div className={styles.spanner}>
				<InspectIcon />
			</div>
			<div className={styles.info}>
				<div className={styles.content}>
					<div className={styles.date}>{date ? formatShortDate(date) : LocalT.translate('unknownDate')}</div>
					<div className={styles.mileageType}>
						<span>
							{LocalT.translate(mileage ? 'record.mileage' : 'unknownMileage', { mileage: numberWithCommas(mileage) })}
						</span>
						{'|'}
						<span>{getServiceType(type)}</span>
					</div>
				</div>
				{isOverdueBadgeVisible && (
					<div>
						<Badge
							text={LocalT.translate('record.overdue', {
								unit: overdue.join(String(LocalT.translate('record.overdueAnd') ?? '')),
							})}
							variant="warning"
						/>
					</div>
				)}
			</div>
		</div>
	);
};
